import { useCallback, useEffect, useState } from 'react';

// A tiny async-data hook for the console screens: runs a loader (an api.ts call),
// tracks loading/error/data, aborts in-flight work on unmount or dependency change,
// and exposes `reload` for the error-state retry button and post-mutation refresh.
// All data flows through api.ts → @bookingdei/api-client (CB-2); this hook never
// touches the network. Duplicated per surface (CB-D3).
export interface ResourceState<T> {
  data: T | undefined;
  error: unknown;
  loading: boolean;
  reload: () => void;
}

export function useResource<T>(
  loader: (signal: AbortSignal) => Promise<T>,
  deps: readonly unknown[],
): ResourceState<T> {
  const [data, setData] = useState<T>();
  const [error, setError] = useState<unknown>();
  const [loading, setLoading] = useState(true);
  const [nonce, setNonce] = useState(0);

  const reload = useCallback(() => setNonce((n) => n + 1), []);

  useEffect(() => {
    const controller = new AbortController();
    let active = true;
    setLoading(true);
    setError(undefined);
    loader(controller.signal)
      .then((result) => {
        if (active) {
          setData(result);
          setLoading(false);
        }
      })
      .catch((err) => {
        if (active && !controller.signal.aborted) {
          setError(err);
          setLoading(false);
        }
      });
    return () => {
      active = false;
      controller.abort();
    };
  }, [...deps, nonce]);

  return { data, error, loading, reload };
}
